/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { useState } from "react";
import {
  ArrowLeft,
  Share2,
  Check,
  Sparkles,
  Menu,
  X,
} from "lucide-react";
import { cn } from "../utils/cn";

interface DocNavbarProps {
  user?: { username: string; email?: string };
  onBackToDashboard: () => void;
  onLogout?: () => void;
  onShare: () => void;
  isCopied: boolean;
  specId: string | null;
  onOpenAIChat: () => void;
  onToggleSidebar?: () => void;
  onLoginClick?: () => void;
}

export function DocNavbar({
  user,
  onBackToDashboard,
  onShare,
  isCopied,
  specId,
  onOpenAIChat,
  onToggleSidebar,
  onLoginClick,
}: DocNavbarProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-zinc-200">
      <div className="h-16 px-4 sm:px-6 flex items-center justify-between">
        {/* Left: Back button */}
        <div className="flex items-center gap-3">
          <button
            onClick={onToggleSidebar}
            className="lg:hidden flex items-center justify-center w-10 h-10 border border-zinc-200 hover:border-zinc-900 transition-colors"
          >
            <Menu className="w-4 h-4 text-zinc-900" />
          </button>
          <button
            onClick={onBackToDashboard}
            className="flex items-center gap-3 text-zinc-900 hover:text-zinc-600 transition-colors group"
          >
            <div className="w-10 h-10 border border-zinc-200 flex items-center justify-center group-hover:border-zinc-900 transition-colors">
              <ArrowLeft className="w-4 h-4" />
            </div>
            <span className="hidden sm:inline text-xs font-medium tracking-wider">DASHBOARD</span>
          </button>
        </div>

        {/* Center: DocGether Logo */}
        <div className="absolute left-1/2 -translate-x-1/2">
          <img
            src="/docgether.png"
            alt="DocGether"
            className="h-10 w-auto rounded-lg"
          />
        </div>

            {/* Right: Actions */}
        <div className="flex items-center gap-3">
          {/* User or Sign In */}
          {user ? (
            <div className="hidden md:flex items-center gap-3 mr-2">
              <div className="w-8 h-8 bg-zinc-900 text-white flex items-center justify-center text-xs font-medium">
                {user.username?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="text-sm text-zinc-900">{user.username}</span>
            </div>
          ) : (
            <button
              onClick={onLoginClick}
              className="hidden md:block px-4 py-2 bg-zinc-900 text-white text-xs font-medium tracking-wider hover:bg-zinc-800 transition-colors mr-2"
            >
              SIGN IN
            </button>
          )}

          {/* Share Button */}
          {specId && (
            <button
              onClick={onShare}
              className={cn(
                "hidden sm:flex items-center gap-2 px-4 py-2 border text-xs font-medium tracking-wider transition-all",
                isCopied
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-zinc-200 text-zinc-700 hover:border-zinc-900"
              )}
            >
              {isCopied ? (
                <Check className="w-3.5 h-3.5" />
              ) : (
                <Share2 className="w-3.5 h-3.5" />
              )}
              {isCopied ? "COPIED" : "SHARE"}
            </button>
          )}

          {/* AI Chat Button - Only for logged-in users */}
          {user && (
            <button
              onClick={onOpenAIChat}
              className="flex items-center gap-2 px-4 py-2 bg-zinc-900 text-white text-xs font-medium tracking-wider hover:bg-zinc-800 transition-colors"
            >
              <Sparkles className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">AI</span>
            </button>
          )}

          {/* Mobile Menu */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="sm:hidden w-10 h-10 border border-zinc-200 flex items-center justify-center"
          >
            {mobileMenuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="sm:hidden border-t border-zinc-200 px-4 py-4 space-y-3 bg-white">
          {user ? (
            <div className="flex items-center gap-3 pb-3 border-b border-zinc-100">
              <div className="w-8 h-8 bg-zinc-900 text-white flex items-center justify-center text-xs font-medium">
                {user.username?.[0]?.toUpperCase() || "U"}
              </div>
              <span className="text-sm text-zinc-900">{user.username}</span>
            </div>
          ) : (
            <button
              onClick={() => { onLoginClick?.(); setMobileMenuOpen(false); }}
              className="w-full px-4 py-2 bg-zinc-900 text-white text-xs font-medium tracking-wider"
            >
              SIGN IN
            </button>
          )}
          {specId && (
            <button
              onClick={() => { onShare(); setMobileMenuOpen(false); }}
              className="w-full flex items-center gap-2 px-4 py-2 border border-zinc-200 text-xs font-medium tracking-wider"
            >
              {isCopied ? <Check className="w-3.5 h-3.5" /> : <Share2 className="w-3.5 h-3.5" />}
              {isCopied ? "COPIED" : "SHARE"}
            </button>
          )}
          <button
            onClick={() => { onBackToDashboard(); setMobileMenuOpen(false); }}
            className="w-full text-left text-xs font-medium tracking-wider text-zinc-600"
          >
            BACK TO DASHBOARD
          </button>
        </div>
      )}
    </header>
  );
}

